import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  COLUMN_WIDTH as BOARD_COLUMN_WIDTH,
  Icon,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  ScrollArea,
  Separator,
  Skeleton,
  SkeletonGroup,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import type { MapPin } from "./map-types";
import type { MapIssue } from "./map-issue";
import { MapIssueCard } from "./map-issue-card";
import {
  useMapInfoboxData,
  type AddressDetails,
} from "#app/lib/hooks/use-map-infobox-data";
import { useIssueView } from "#app/components/issue-view";
import { useIssueCardContext } from "#app/components/issue-card/issue-card-context";
import { copyText } from "#app/components/entity/row-actions";
import { openDirections } from "#app/lib/utils/open-directions";
import {
  directionsAddress,
  infoboxTitleBlock,
  propertyDetailGroups,
  PROPERTY_DETAIL_SKELETON_ROWS,
  resolvePropertyDetails,
  type PropertyDetailGroup,
} from "./map-property-details";
import { LABEL_SEGMENT_HEIGHT, LABEL_SEGMENT_MAX_WIDTH } from "#app/components/issue-card";
import { AnalyticsContext, MapEvent, trackEvent } from "#app/lib/analytics";

/**
 * Width of the map infobox popup (used for centering in map-view.tsx).
 *
 * Derived from the board, not chosen here: the cards inside are the board's
 * kanban cards VERBATIM, so they get the board-LARGE card footprint — the
 * large column (340) minus its 8px card insets is a 324px card, and this
 * popover's 12px insets wrap that back to 348. A card the board never
 * renders at this width would read as a different card.
 */
export const INFOBOX_WIDTH = BOARD_COLUMN_WIDTH + 8;

/** The project scroll zone's cap: about two board cards, the next cut by
 *  the fade so the clipping itself says "more below". */
const PROJECTS_MAX_HEIGHT = 340;
/** The dossier's label gutter — values left-anchor here so the eye travels
 *  two vertical lines, never the card's width. The card token's label segment
 *  caps at the same width, so a dossier and a card token align. */
const DOSSIER_LABEL_WIDTH = LABEL_SEGMENT_MAX_WIDTH;


type MapInfoboxContentProps = Omittable<{
  pin: MapPin;
  /** The map's Property facts layer (see `MapSettings.propertyFacts`). */
  showPropertyFacts?: boolean;
  onClose?: () => void;
  onAddProject?: () => void;
}>;

// ---------------------------------------------------------------------------
// Header — the location
// ---------------------------------------------------------------------------

function MapInfoboxHeader({
  pin,
  addressDetails,
  dossierRows,
  propertyDetailsLoading,
  showPropertyFacts = true,
  hasIssues,
  onClose,
}: Omittable<{
  pin: MapPin;
  addressDetails: AddressDetails | null;
  /** Every property fact Melissa answered with, already listed. */
  dossierRows: PropertyDetailGroup["rows"];
  propertyDetailsLoading: boolean;
  /** The map's Property facts layer. Off = no property button. */
  showPropertyFacts?: boolean;
  /** A pin with projects keeps a compact header and carries the facts
   *  behind the property button; an EMPTY pin is the place's own card — the
   *  address grows to the hero rung and the facts are listed below. */
  hasIssues: boolean;
  onClose?: () => void;
}>) {
  const { title, subtitle: cityLine } = infoboxTitleBlock(pin, addressDetails);

  // The button exists while there is (or may yet be) something to read — the
  // same rule as the empty pin's dossier zone. An address Melissa has nothing
  // on says nothing about it: no button, no empty popover.
  const showPropertyButton =
    hasIssues &&
    showPropertyFacts &&
    (dossierRows.length > 0 || propertyDetailsLoading);

  return (
    <Box
      direction="column"
      gap="2xs"
      paddingTop="base"
      paddingX="base"
      paddingBottom="sm"
      shrink={false}
    >
      <Box direction="row" align="start" justify="between" gap="sm">
        {/* Identity is never lost: the street wraps to two lines and stops —
            the suite or unit is the part a rep needs at the door. */}
        <Text size={hasIssues ? "sm" : "lg"} weight="semibold" truncate={2}>
          {title}
        </Text>
        <Box direction="row" align="center" gap="2xs" shrink={false}>
          {showPropertyButton ? (
            // On a pin with projects the facts are context, not the content,
            // so they wait behind one glyph beside Close and open as the
            // SAME listed dossier the empty pin shows in place. Reading is
            // never put in motion.
            <Popover
              onOpenChange={(open) => {
                if (!open) return;
                trackEvent({
                  context: AnalyticsContext.Map,
                  event: MapEvent.OpenPropertyDetails,
                  data: { hasProjects: true },
                });
              }}
            >
              <Tooltip>
                <TooltipTrigger
                  render={
                    <PopoverTrigger
                      render={
                        <Button
                          aria-label="Property details"
                          size="icon-xs"
                          color="neutral"
                        />
                      }
                    />
                  }
                >
                  <Icon name="info" />
                </TooltipTrigger>
                <TooltipPopup>Property details</TooltipPopup>
              </Tooltip>
              <PopoverPopup align="end" padding="none" width="card">
                <MapInfoboxDossier
                  rows={dossierRows}
                  loading={propertyDetailsLoading}
                />
              </PopoverPopup>
            </Popover>
          ) : null}
          {onClose ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    aria-label="Close"
                    onClick={(event) => {
                      event.stopPropagation();
                      onClose();
                    }}
                    size="icon-xs"
                    color="neutral"
                  />
                }
              >
                <Icon name="x" />
              </TooltipTrigger>
              <TooltipPopup>Close</TooltipPopup>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
      {cityLine ? (
        <Text size="xs" variant="muted" truncate>
          {cityLine}
        </Text>
      ) : null}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Dossier — the listed facts on an EMPTY pin
// ---------------------------------------------------------------------------

/**
 * The scout's reading surface: every property fact clearly LISTED on a label
 * gutter with left-anchored values, fenced by the popover's full-width rails.
 * One anatomy for every host: in place between the rails on an EMPTY pin,
 * inside the header's property popover on a pin with projects, and under
 * the phone sheet's rail (`MapInfoboxSheet`).
 * The vertical padding is symmetric on purpose — one lone fact floats
 * centered between the rails instead of clinging to the top. No data at all
 * folds the whole zone away (the caller drops it); absence costs nothing.
 *
 * Row pitch is 28px, built from SYMMETRIC spacing (4px of vertical padding
 * on the value cell + 4px column gap) rather than a row min-height: baseline
 * alignment parks the 16px line at the top of a taller row, so a min-height
 * would leave the last row's slack under the text and the zone's bottom
 * padding would read fatter than its top.
 */
export function MapInfoboxDossier({
  rows,
  loading,
  inset = "base",
}: {
  rows: PropertyDetailGroup["rows"];
  loading: boolean;
  /** The side gutter: the popover's `base` by default; the phone sheet
   *  passes `lg` so the list lands on its DrawerHeader's 24px edge. */
  inset?: "base" | "lg";
}) {
  return (
    <Box
      direction="column"
      justify="center"
      paddingX={inset}
      paddingY="md"
      shrink={false}
    >
      {rows.length === 0 && loading ? (
        // The scaffold stands on the settled rows' own pitch — a 24px cell
        // (the value cell's 4px padding around its 16px line) on the 2px row
        // gap — so the four facts Melissa can answer swap in without moving
        // the popover's bottom edge. Anonymous bars: the scaffold must not
        // NAME fields it cannot promise.
        <SkeletonGroup aria-label="Property details loading">
          <Box direction="column" gap="2xs">
            {PROPERTY_DETAIL_SKELETON_ROWS.map((row, i) => (
              <Box
                key={i}
                direction="row"
                align="center"
                height={24}
                gap="xs"
              >
                <Box width={DOSSIER_LABEL_WIDTH} shrink={false}>
                  <Skeleton height={12} width={row.labelWidth} />
                </Box>
                <Box paddingX="xs">
                  <Skeleton height={12} width={row.valueWidth} />
                </Box>
              </Box>
            ))}
          </Box>
        </SkeletonGroup>
      ) : (
        <Box direction="column" gap="2xs">
          {rows.map((row) => (
            <Box
              key={row.label}
              direction="row"
              align="baseline"
              gap="xs"
            >
              <Box width={DOSSIER_LABEL_WIDTH} shrink={false}>
                <Text size="xs" variant="muted">
                  {row.label}
                </Text>
              </Box>
              {/* The VALUE is the copy button — the label is a caption and
                  stays inert. The cell claims the rest of the row so the
                  pointer lands on it wherever the value ends, and it shows
                  its hand on hover: wash, pointer, copy glyph at its right
                  edge, tooltip. Enter copies too. */}
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Box
                      as="button"
                      type="button"
                      aria-label={`Copy ${row.label.toLowerCase()}`}
                      grow
                      minWidth={0}
                      paddingX="xs"
                      // The cell, not the row, carries the vertical padding:
                      // 4px around the 16px line stands the hover wash 24px
                      // tall on the 28px pitch (with the 4px row gap).
                      paddingY="xs"
                      group="row"
                      hoverBackground
                      position="relative"
                      rounded="sm"
                      onClick={() => {
                        void copyText(row.value, row.label).then((ok) => {
                          if (!ok) return;
                          trackEvent({
                            context: AnalyticsContext.Map,
                            event: MapEvent.CopyFact,
                            data: { fact: row.label },
                          });
                        });
                      }}
                      // Box as="button" resets the UA outline and authors no
                      // ring of its own; the cell copies on Enter, so it
                      // needs the app's focus treatment.
                      className="focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  }
                >
                  {/* ONE value voice for the whole list, on the SAME 12px
                      rung as the label — weight alone separates the two
                      columns; a size step on the aligned gutter reads as two
                      disjointed lists (Spencer, 2026-09-09). Long values (the
                      owner) just wrap. */}
                  <Text size="xs" weight="medium">
                    {row.value}
                  </Text>
                  {/* The glyph is a cue, not the target: it floats over the
                      cell's right edge instead of taking a column, so a slot
                      that is empty until hover never wraps a value early. */}
                  <Box
                    align="center"
                    bottom={0}
                    direction="row"
                    hoverReveal="row"
                    position="absolute"
                    right={4}
                    top={0}
                  >
                    <Icon name="copy" size="sm" color="muted" />
                  </Box>
                </TooltipTrigger>
                <TooltipPopup side="bottom">{`Copy ${row.label.toLowerCase()}`}</TooltipPopup>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Projects — the capped card scroll on a PROJECT pin
// ---------------------------------------------------------------------------

/** The title rail's line box: `Text size="sm"` on the card. */
const CARD_TITLE_LINE = 20;
/** One body row on the card: the token's label-segment rung. */
const CARD_ROW = LABEL_SEGMENT_HEIGHT;

/**
 * A card-shaped placeholder built on the SAME anatomy as the settled
 * `IssueCard` (`surface="popover"`, bare list tokens): the title rail (the
 * card's 10px rail padding around a 24px title block), one 28px row per body
 * token the current card display options will render, 2px between rows, and
 * the 28px footer under its 12px lead — so the settled card lands on the
 * skeleton's exact height and the popover's bottom edge never moves. The
 * row count reads the display options the map's `IssueCardProvider` holds:
 * every shown property that a project carries a value for by construction
 * (stage, workflow, type; the dates) and every shown custom field. Teams
 * and the address render only when the project has one, so they are not
 * counted — a card that turns out to carry them grows by a row, which is
 * the cost of not knowing, never a guess that shrinks.
 */
function IssueCardSkeleton() {
  const { displayOptions, customFieldDefs } = useIssueCardContext();
  const rows =
    [
      displayOptions.showStage,
      displayOptions.showWorkflow,
      displayOptions.showProjectType,
      displayOptions.showCreated,
      displayOptions.showUpdated,
    ].filter(Boolean).length + customFieldDefs.length;
  return (
    <Box
      surface="card"
      border
      rounded="sm"
      paddingX="xs"
      paddingBottom="sm"
      // Mirrors the settled card's popover re-grounding (IssueCard
      // surface="popover"): light on the card fill with the rail rung's
      // default border, dark on the popover fill with the strong hairline.
      // Box `surface` scopes ink and hairlines only, so the fill is explicit
      // — the projects zone beneath is muted, not white.
      className="bg-card dark:border-border-strong dark:bg-popover"
    >
      <Box paddingLeft="sm" className="py-2.5">
        <Box height={CARD_TITLE_LINE + 8} justify="center">
          <Skeleton height={14} width={140} />
        </Box>
      </Box>
      {Array.from({ length: rows }, (_, i) => (
        <Box
          key={i}
          data-slot="issue-card-skeleton-row"
          direction="row"
          align="center"
          height={CARD_ROW}
          paddingX="xs"
          gap="sm"
          marginTop={i === 0 ? undefined : "2xs"}
        >
          <Skeleton height={16} width={16} />
          <Skeleton
            height={12}
            vary={i}
            width={90}
          />
        </Box>
      ))}
      <Box
        direction="row"
        align="center"
        justify="between"
        minHeight={CARD_ROW}
        paddingX="xs"
        paddingTop={rows > 0 ? "md" : "sm"}
      >
        <Box direction="row" align="center" gap="base">
          <Skeleton height={12} width={48} />
          <Skeleton height={12} width={28} />
        </Box>
        <Skeleton circle width={24} />
      </Box>
    </Box>
  );
}

/**
 * The hunter's zone: the kanban cards in a scroll capped at about three
 * cards, fenced by the popover's full-width rails. Past the cap the bottom
 * fade says "more below" and a quiet remainder counts DOWN what is still out
 * of view — it vanishes at the end of the scroll, so it reads as what is
 * left, never as chrome.
 */
function MapInfoboxProjects({
  pin,
  issues,
  onIssueClick,
}: {
  pin: MapPin;
  issues: MapIssue[];
  onIssueClick: (issue: MapIssue) => void;
}) {
  // The card count is on the pin before any fetch, so the zone has its shape
  // from the first frame; cards swap in from cache or over the skeletons.
  const placeholders = issues.length === 0 ? pin.issueIds.length : 0;
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [remaining, setRemaining] = useState(0);

  // Cards fully below the visible bottom. Rect-based so the scroll viewport
  // (a Base UI part this component does not render) needs no coordinate
  // bookkeeping; recounted on scroll (captured — scroll does not bubble) and
  // whenever the card list changes.
  const recount = useCallback(() => {
    const wrap = wrapRef.current;
    const list = listRef.current;
    if (!wrap || !list) return;
    const viewport = wrap.querySelector('[data-slot="scroll-area-viewport"]');
    if (!viewport) return;
    const bottom = viewport.getBoundingClientRect().bottom;
    let below = 0;
    for (const child of Array.from(list.children)) {
      if (child.getBoundingClientRect().top >= bottom - 6) below += 1;
    }
    setRemaining(below);
  }, []);
  // Recount whenever the geometry settles or changes: the first pass can run
  // before the scroll viewport has its height (counting everything as
  // "below"), and no scroll event ever corrects a wrong initial answer.
  useEffect(() => {
    recount();
    const wrap = wrapRef.current;
    const list = listRef.current;
    if (!wrap || !list) return;
    const viewport = wrap.querySelector('[data-slot="scroll-area-viewport"]');
    const observer = new ResizeObserver(recount);
    observer.observe(list);
    if (viewport) observer.observe(viewport);
    return () => observer.disconnect();
  }, [recount, issues.length, placeholders]);

  return (
    <Box
      ref={wrapRef}
      direction="column"
      paddingX="base"
      paddingY="sm"
      position="relative"
      minHeight={0}
      shrink
      // The zone recedes ONE gentle step so the cards lift out of it instead
      // of sitting flush: dark = the elevated rung (~1.7L under the popover;
      // the card re-grounds at the popover's own fill), light = the muted
      // rung (~3.8L under the white popover — `background` and `elevated`
      // sit within 2L of it and still read as white; the card keeps its
      // white fill and rail-rung border). Spencer, 2026-09-09.
      className="bg-muted dark:bg-elevated"
      onScrollCapture={recount}
    >
      <Box direction="column" maxHeight={PROJECTS_MAX_HEIGHT} minHeight={0}>
        <ScrollArea scrollFade>
          <Box ref={listRef} direction="column" gap="xs">
            {placeholders > 0 ? (
              <SkeletonGroup aria-label="Projects loading">
                <Box direction="column" gap="xs">
                  {Array.from({ length: placeholders }, (_, i) => (
                    <IssueCardSkeleton key={i} />
                  ))}
                </Box>
              </SkeletonGroup>
            ) : (
              issues.map((issue) => (
                <MapIssueCard
                  key={issue.issueId}
                  issue={issue}
                  onClick={onIssueClick}
                />
              ))
            )}
          </Box>
        </ScrollArea>
      </Box>
      {remaining > 0 ? (
        <Box
          position="absolute"
          left={0}
          right={0}
          bottom={12}
          direction="row"
          justify="center"
          pointerEvents="none"
        >
          {/* Seated higher in the fade, on the readable muted rung — over
              the dimmed card content a 10px faint whisper disappeared
              (Spencer, 2026-09-09). */}
          <Text size="xs" weight="medium" variant="muted">
            {remaining} more
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Footer — the actions
// ---------------------------------------------------------------------------

function MapInfoboxFooter({
  pin,
  addressDetails,
  hasIssues,
  onAddProject,
}: Omittable<{
  pin: MapPin;
  addressDetails: AddressDetails | null;
  hasIssues: boolean;
  onAddProject?: () => void;
}>) {
  const destination = directionsAddress(pin, addressDetails);
  return (
    <Box
      direction="row"
      gap="xs"
      paddingX="base"
      paddingTop="sm"
      paddingBottom="base"
      shrink={false}
    >
      {onAddProject ? (
        // Filled only when the address has no Project yet — that is the one
        // thing to do there. Beside existing Projects it steps back to outline.
        <Box direction="column" fill="flex">
          <Button
            onClick={onAddProject}
            shape="square"
            variant={hasIssues ? "outline" : "solid"}
          >
            <Icon name="plus" />
            Add Project
          </Button>
        </Box>
      ) : null}
      <Box direction="column" fill="flex">
        <Button
          disabled={!destination}
          onClick={() => {
            if (!destination) return;
            trackEvent({
              context: AnalyticsContext.Map,
              event: MapEvent.GetDirections,
              data: { hasProjects: hasIssues },
            });
            openDirections(destination);
          }}
          shape="square"
          variant="outline"
        >
          <Icon name="navigation" />
          Directions
        </Button>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface MapInfoboxContentViewProps extends MapInfoboxContentProps {
  issues: MapIssue[];
  addressDetails: AddressDetails | null;
  propertyDetailsLoading: boolean;
  /** Called when an issue card is clicked. */
  onIssueClick: (issue: MapIssue) => void;
}

/**
 * Pure presentational infobox — receives pre-fetched data as props. One rail
 * anatomy for both pin kinds: header / full-width hairline / content /
 * hairline / actions, where only the content zone changes. A pin WITH
 * projects carries the kanban cards in a capped scroll and the property facts
 * behind the header's property button (a popover listing the same dossier);
 * an EMPTY pin is the place's card — hero address, the facts clearly LISTED
 * between the rails — and a pin with neither folds the middle away entirely
 * (header, one rail, actions).
 */
export const MapInfoboxContentView = memo(function MapInfoboxContentView({
  pin,
  issues,
  addressDetails,
  propertyDetailsLoading,
  showPropertyFacts,
  onClose,
  onAddProject,
  onIssueClick,
}: MapInfoboxContentViewProps) {
  const hasIssues = pin.issueIds.length > 0;
  const details = resolvePropertyDetails(pin, addressDetails);
  const dossierRows = useMemo(
    () => propertyDetailGroups(details).flatMap((group) => group.rows),
    [details],
  );
  // The scout's dossier zone exists while there is (or may yet be) something
  // to read. Zero data — or the Property facts layer off — folds it away:
  // header, one rail, actions. Absence costs nothing.
  const showDossier =
    !hasIssues &&
    showPropertyFacts !== false &&
    (dossierRows.length > 0 || propertyDetailsLoading);

  return (
    <>
      <MapInfoboxHeader
        pin={pin}
        addressDetails={addressDetails}
        dossierRows={dossierRows}
        propertyDetailsLoading={propertyDetailsLoading}
        showPropertyFacts={showPropertyFacts}
        hasIssues={hasIssues}
        onClose={onClose}
      />
      <Separator />
      {hasIssues ? (
        <>
          <MapInfoboxProjects
            pin={pin}
            issues={issues}
            onIssueClick={onIssueClick}
          />
          <Separator />
        </>
      ) : showDossier ? (
        <>
          <MapInfoboxDossier
            rows={dossierRows}
            loading={propertyDetailsLoading}
          />
          <Separator />
        </>
      ) : null}
      <MapInfoboxFooter
        pin={pin}
        addressDetails={addressDetails}
        hasIssues={hasIssues}
        onAddProject={onAddProject}
      />
    </>
  );
});

/**
 * Self-contained infobox that fetches its own data via Apollo.
 * Use only when rendered inside the ApolloProvider tree (e.g. the map InfoWindow).
 */
export function MapInfoboxContent(props: MapInfoboxContentProps) {
  const { issues, addressDetails, propertyDetailsLoading } =
    useMapInfoboxData(props.pin);
  const { openIssue } = useIssueView();

  // No imperative re-center — the map screen owns a single effect that pans
  // whenever (selectedPin, drawerWidth) changes. Opening the drawer flips
  // drawerWidth, which re-fires that effect.
  const handleIssueClick = useCallback(
    (issue: MapIssue) => openIssue(issue, { source: "map" }),
    [openIssue],
  );

  // Stop wheel events from propagating to the Google Map (which captures
  // them for zoom), so the well inside the infobox can scroll.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const stop = (e: WheelEvent) => e.stopPropagation();
    el.addEventListener("wheel", stop, { passive: true });
    return () => el.removeEventListener("wheel", stop);
  }, []);

  return (
    // A flex column capped so the WHOLE popover always fits above its pin:
    // the selection flight parks the pin ~72% down the map, so the space
    // above it is ~72dvh minus the anchor gap, caret and a top margin that
    // clears the floating search band. Tall
    // windows rest at 60dvh; short windows bind on the parked-space term.
    // Header and footer keep their height; the projects zone is the one
    // shrinking item and scrolls inside its own bounds.
    <Box
      ref={containerRef}
      direction="column"
      surface="popover"
      border
      rounded="lg"
      overflow="clip"
      touchAction="manipulation"
      maxHeight="min(60dvh, 72dvh - 128px)"
      width={INFOBOX_WIDTH}
    >
      <MapInfoboxContentView
        {...props}
        addressDetails={addressDetails}
        issues={issues}
        onIssueClick={handleIssueClick}
        propertyDetailsLoading={propertyDetailsLoading}
      />
    </Box>
  );
}
